/**
 * Tech Câmbio Design System — camada semântica do tema.
 * ---------------------------------------------------------------------------
 * Esta é a CAMADA 2 da estrutura de tema (ver docs/ESTRUTURA-TEMA.md):
 *
 *   1. Marca      → theme.json do tenant. Só o que é decisão de identidade.
 *   2. Semântica  → este arquivo. Igual para todos os tenants.
 *   3. Componente → derivado, não escrito. Ver expand.js.
 *
 * POR QUE ISTO SAIU DO theme.json
 * A medição dos 8 tenants em 14/08/2026: de 349 chaves de cor por tenant, 226
 * (65%) eram idênticas em todos e 115 (33%) divergiam sem intenção — o mesmo
 * status "cadastro enviado" era rosa em dois tenants e cinza em três, porque
 * foram copiados em épocas diferentes. Só 7 chaves (2%) tinham valor próprio
 * por tenant: logo, símbolo e primary.
 *
 * Estado semântico não é decisão de marca: "recusado" é vermelho em qualquer
 * tenant. Manter isso no arquivo de cada um só multiplica a chance de deriva.
 */

/**
 * Paleta de estados do DS. Espelha os tokens --tc-* de styles/tokens.css —
 * aqui em JS porque os slots do Vuetify precisam de hex, não de var().
 *
 * ATENÇÃO: é extensão de produto. O Manual de Marca só define os azuis; estes
 * estados foram calibrados para conviver com eles e ainda dependem de aval do
 * cliente antes de produção.
 */
export const SEMANTIC = {
  light: {
    success: { solid: '#1F7A52', soft: '#E8F3EC' },
    warning: { solid: '#946312', soft: '#FAF1DC', strong: '#C9962F' },
    danger: { solid: '#B23A2E', soft: '#FAEAE7' },
    info: { solid: '#0047BB', soft: '#E7ECF8' },
    neutral: { solid: '#62626B', soft: '#F0EFEA' },

    text: '#101820',
    textMuted: '#62626B',
    textFaint: '#8C8C93',
    line: '#E7E6E1',
    bg: '#FAF9F6',
    surface: '#FFFFFF',
    surfaceRaised: '#F4F3EF'
  },

  dark: {
    success: { solid: '#5FD3A0', soft: '#1E3D4B' },
    warning: { solid: '#E3C390', soft: '#3A3428', strong: '#E3C390' },
    danger: { solid: '#E88A7E', soft: '#343347' },
    info: { solid: '#6FA8FF', soft: '#21385B' },
    neutral: { solid: '#C3C9D2', soft: '#1A2E4A' },

    text: '#FFFFFF',
    textMuted: '#C3C9D2',
    textFaint: '#8E97A5',
    line: '#33445C',
    bg: '#0C2340',
    surface: '#12233C',
    surfaceRaised: '#1A2E4A'
  }
};

/**
 * Cada chave de status do produto → o estado semântico que ela representa.
 *
 * É o SEGUNDO salto do mapeamento. O primeiro (id do workflow → chave de
 * status) já vive em código, em constants/operation-status-colors.js. Só este
 * estava no theme.json, replicado 8×; junto com o outro, o caminho inteiro fica
 * num lugar só.
 *
 * Padroniza de quebra o que estava incoerente: "em análise" era azul no cliente
 * e cinza na operação.
 */
export const STATUS_SEMANTIC = {
  // concluído / aprovado / ativo / positivo
  cadastroFinalizado: 'success',
  cadastroAprovado: 'success',
  liquidada: 'success',
  active: 'success',
  indicator: 'success',
  buy: 'success',

  // pendente / aguardando ação
  cadastroEmAndamento: 'warning',
  pendenciaDocumental: 'warning',
  pendenciaBanco: 'warning',
  comPendencias: 'warning',
  cadastroPreAprovado: 'warning',
  signature: 'warning',

  // recusado / cancelado / inativo
  cadastroRecusado: 'danger',
  cancelada: 'danger',
  inactive: 'danger',
  sell: 'danger',

  // em processamento / em análise / enviado
  cadastroEnviado: 'info',
  cadastroEmAnalise: 'info',
  emAnalise: 'info',
  analisePendencias: 'info',
  enviadoAoBanco: 'info',
  emLiquidacao: 'info',
  aguardandoAssinatura: 'info',
  aguardando: 'info',
  atualizacaoCadastral: 'info',
  corbanMaster: 'info',
  automatic: 'info',

  // sem estado / encerrado / padrão
  pldCompliance: 'neutral',
  encerrado: 'neutral',
  standard: 'neutral'
};

/** Par fundo/texto de um estado, no formato `tonal` que o app já consome. */
export function tonal(estado, variante = 'light') {
  const p = SEMANTIC[variante] || SEMANTIC.light;
  const e = p[estado] || p.neutral;

  return { chip: e.soft, text: e.solid };
}

/** Tonal a partir de uma chave de status do produto. */
export function tonalDeStatus(chaveStatus, variante = 'light') {
  return tonal(STATUS_SEMANTIC[chaveStatus] || 'neutral', variante);
}

export default { SEMANTIC, STATUS_SEMANTIC, tonal, tonalDeStatus };
